import { useEffect, useState } from "react";
import { Badge, Button, Divider, Dropdown, Input, Option, ProgressBar, Spinner, Switch, Text, mergeClasses } from "@fluentui/react-components";
import type {
  LiteSession,
  MarketplaceEntitlement,
  MeetingReport,
  PredefinedUser,
  MyCreditStatus,
  TenantCreditSummary,
  UserCreditInfo,
  Language,
} from "../services/apiService";
import {
  extractChatIdFromJoinUrl,
  getTrackedMeetingStatus,
  setEmailScope,
  isAdminRole,
  canManageCredits,
  getOrgDefaults,
  updateOrgDefaults,
  getPredefinedUsers,
  addPredefinedUser,
  removePredefinedUser,
  getMyCredits,
  getTenantCredits,
  setUserCreditAllocation,
  getMe,
  getLanguages,
  updateMyLanguage,
} from "../services/apiService";
import { openExternalLink } from "../teams/teamsClient";
import { config } from "../config";
import { useUiStyles, IconTile, ShieldIcon, RocketIcon, SectionTitle, Footer } from "./ui";
import { useLanguage, type UiLanguage } from "../i18n/LanguageContext";


interface SettingsPageProps {
  session: LiteSession;
  entitlement: MarketplaceEntitlement | null;
  reports: MeetingReport[];
  insideTeams: boolean;
  myRole: number | null;
  onRefreshEntitlement: () => Promise<void>;
}


function statusLabel(status: string | null | undefined, t: (path: string) => string): string {
  switch (status) {
    case "Subscribed": return t("settings.license.statusSubscribed");
    case "PendingFulfillmentStart": return t("settings.license.statusPending");
    case "Suspended": return t("settings.license.statusSuspended");
    case "Unsubscribed": return t("settings.license.statusCancelled");
    default: return t("settings.license.statusNone");
  }
}

function statusColor(status: string | null | undefined): "success" | "warning" | "danger" | "informative" {
  switch (status) {
    case "Subscribed": return "success";
    case "PendingFulfillmentStart": return "warning";
    case "Suspended": return "danger";
    default: return "informative";
  }
}

interface RecipientRow {
  eventId: string;
  chatId: string;
  name: string;
  loading: boolean;
  saving: boolean;
  tracked: boolean;
  sendToAll: boolean;
}

/**
 * "Settings" static tab: purchased license/plan, the granted Graph/RSC
 * permissions, and — per tracked meeting — who receives the AI summary.
 * Always reachable (even without an active subscription) so users know what
 * to buy and can review permissions before purchasing.
 */
export function SettingsPage({ session, entitlement, reports, insideTeams, myRole, onRefreshEntitlement }: SettingsPageProps) {
  const s = useUiStyles();
  const { t, tRaw, language, setLanguage } = useLanguage();
  const summaryTemplateLabels = tRaw<Record<number, string>>("settings.orgDefaults.templates");
  const deliveryModeLabels = tRaw<Record<number, string>>("settings.orgDefaults.deliveryModes");
  const [refreshing, setRefreshing] = useState(false);
  const [recipientRows, setRecipientRows] = useState<RecipientRow[]>([]);
  const isAdmin = isAdminRole(myRole);
  const canManageOrgCredits = canManageCredits(myRole);

  // AI summary language — the full backend Language list + this user's current pick.
  const [languages, setLanguages] = useState<Language[] | null>(null);
  const [myLanguageId, setMyLanguageId] = useState<string | null>(null);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [languageSaved, setLanguageSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getLanguages(session.jwt), getMe(session.jwt)])
      .then(([list, me]) => {
        if (cancelled) return;
        setLanguages(list);
        setMyLanguageId(me.languageId ?? null);
      })
      .catch((err) => {
        if (!cancelled) setLanguageError(err instanceof Error ? err.message : t("settings.summaryLanguage.error"));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.jwt]);

  const saveMyLanguage = async (languageId: string) => {
    setMyLanguageId(languageId); // optimistic
    setSavingLanguage(true);
    setLanguageError(null);
    setLanguageSaved(false);
    try {
      await updateMyLanguage(session.jwt, languageId);
      setLanguageSaved(true);
    } catch (err) {
      setLanguageError(err instanceof Error ? err.message : t("settings.summaryLanguage.error"));
    } finally {
      setSavingLanguage(false);
    }
  };

  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [summaryTemplate, setSummaryTemplate] = useState<number | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<number | null>(null);
  const [predefined, setPredefined] = useState<PredefinedUser[] | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [predefinedBusy, setPredefinedBusy] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setOrgLoading(true);
    Promise.all([
      getOrgDefaults(session.jwt, session.user.customerId),
      getPredefinedUsers(session.jwt, session.user.customerId),
    ])
      .then(([defaults, predefinedUsers]) => {
        if (cancelled) return;
        setSummaryTemplate(defaults.summaryTemplate);
        setDeliveryMode(defaults.meetingSendingType);
        setPredefined(predefinedUsers);
      })
      .catch((err) => {
        if (!cancelled) setOrgError(err instanceof Error ? err.message : t("settings.errors.loadOrgSettings"));
      })
      .finally(() => {
        if (!cancelled) setOrgLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, session.jwt, session.user.customerId]);

  const saveOrgDefault = async (patch: { summaryTemplate?: number; meetingSendingType?: number }) => {
    setOrgError(null);
    try {
      await updateOrgDefaults(session.jwt, session.user.customerId, patch);
      if (patch.summaryTemplate !== undefined) setSummaryTemplate(patch.summaryTemplate);
      if (patch.meetingSendingType !== undefined) setDeliveryMode(patch.meetingSendingType);
    } catch (err) {
      setOrgError(err instanceof Error ? err.message : t("settings.errors.saveChange"));
    }
  };

  // Meeting credits — my own usage (everyone) + tenant pool & per-user allocation (Admin/SuperAdmin only).
  const [myCredits, setMyCredits] = useState<MyCreditStatus | null>(null);
  const [tenantCredits, setTenantCredits] = useState<TenantCreditSummary | null>(null);
  const [creditsError, setCreditsError] = useState<string | null>(null);
  const [allocationDrafts, setAllocationDrafts] = useState<Record<string, string>>({});
  const [savingAllocationId, setSavingAllocationId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyCredits(session.jwt)
      .then((status) => {
        if (!cancelled) setMyCredits(status);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session.jwt]);

  const loadTenantCredits = () => {
    let cancelled = false;
    getTenantCredits(session.jwt)
      .then((summary) => {
        if (cancelled) return;
        setTenantCredits(summary);
        setAllocationDrafts(
          Object.fromEntries(summary.allocations.map((a) => [a.userId, String(a.monthlyLimit)])),
        );
      })
      .catch((err) => {
        if (!cancelled) setCreditsError(err instanceof Error ? err.message : t("settings.errors.loadOrgCredits"));
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!canManageOrgCredits) return;
    return loadTenantCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageOrgCredits, session.jwt]);

  const saveAllocation = async (userId: string) => {
    const raw = allocationDrafts[userId] ?? "0";
    const value = Math.max(0, Math.floor(Number(raw) || 0));
    setSavingAllocationId(userId);
    setCreditsError(null);
    try {
      await setUserCreditAllocation(session.jwt, userId, value);
      loadTenantCredits();
      if (userId === session.user.id) {
        getMyCredits(session.jwt).then(setMyCredits).catch(() => undefined);
      }
    } catch (err) {
      setCreditsError(err instanceof Error ? err.message : t("settings.errors.saveLimit"));
    } finally {
      setSavingAllocationId(null);
    }
  };

  const addRecipient = async () => {
    if (!newEmail.trim() || !newName.trim()) return;
    setPredefinedBusy(true);
    setOrgError(null);
    try {
      await addPredefinedUser(session.jwt, session.user.customerId, newEmail.trim(), newName.trim());
      const refreshed = await getPredefinedUsers(session.jwt, session.user.customerId);
      setPredefined(refreshed);
      setNewEmail("");
      setNewName("");
    } catch (err) {
      setOrgError(err instanceof Error ? err.message : t("settings.errors.addRecipient"));
    } finally {
      setPredefinedBusy(false);
    }
  };

  const removeRecipient = async (id: string) => {
    setPredefinedBusy(true);
    setOrgError(null);
    try {
      await removePredefinedUser(session.jwt, id);
      setPredefined((rows) => rows?.filter((r) => r.id !== id) ?? rows);
    } catch (err) {
      setOrgError(err instanceof Error ? err.message : t("settings.errors.removeRecipient"));
    } finally {
      setPredefinedBusy(false);
    }
  };

  const teamsStoreUrl = `https://teams.microsoft.com/l/app/${config.teamsAppId}`;
  const buyUrl = entitlement?.marketplaceListingUrl || teamsStoreUrl;

  useEffect(() => {
    const candidates = reports
      .map((r) => ({ report: r, chatId: extractChatIdFromJoinUrl(r.joinUrl) }))
      .filter((r): r is { report: MeetingReport; chatId: string } => Boolean(r.chatId))
      .slice(0, 8);

    let cancelled = false;
    setRecipientRows(
      candidates.map(({ report, chatId }) => ({
        eventId: report.eventId,
        chatId,
        name: report.name,
        loading: true,
        saving: false,
        tracked: false,
        sendToAll: false,
      })),
    );

    candidates.forEach(async ({ report, chatId }) => {
      try {
        const status = await getTrackedMeetingStatus(session.jwt, chatId);
        if (cancelled) return;
        setRecipientRows((rows) =>
          rows.map((row) =>
            row.eventId === report.eventId
              ? { ...row, loading: false, tracked: status.registered, sendToAll: Boolean(status.sendToAll) }
              : row,
          ),
        );
      } catch {
        if (cancelled) return;
        setRecipientRows((rows) =>
          rows.map((row) => (row.eventId === report.eventId ? { ...row, loading: false, tracked: false } : row)),
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [reports, session.jwt]);

  const handleRefreshEntitlement = async () => {
    setRefreshing(true);
    try {
      await onRefreshEntitlement();
    } finally {
      setRefreshing(false);
    }
  };

  const toggleRecipient = async (row: RecipientRow, next: boolean) => {
    setRecipientRows((rows) =>
      rows.map((r) => (r.eventId === row.eventId ? { ...r, saving: true, sendToAll: next } : r)),
    );
    try {
      const result = await setEmailScope(session.jwt, row.chatId, next);
      setRecipientRows((rows) =>
        rows.map((r) => (r.eventId === row.eventId ? { ...r, saving: false, sendToAll: result.sendToAll } : r)),
      );
    } catch {
      setRecipientRows((rows) =>
        rows.map((r) => (r.eventId === row.eventId ? { ...r, saving: false, sendToAll: !next } : r)),
      );
    }
  };

  const trackedRows = recipientRows.filter((r) => r.tracked);

  return (
    <main className={s.page}>
      <Text size={500} weight="semibold" block style={{ marginBottom: "16px" }}>
        {t("settings.title")}
      </Text>

      {/* App interface language */}
      <section className={s.section}>
        <SectionTitle>{t("settings.language.sectionTitle")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
          <div className={s.rowBetween}>
            <Text weight="medium">{t("settings.language.label")}</Text>
            <Dropdown
              size="small"
              style={{ minWidth: "140px" }}
              value={language === "tr" ? "Türkçe" : "English"}
              selectedOptions={[language]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) setLanguage(data.optionValue as UiLanguage);
              }}
            >
              <Option value="en">English</Option>
              <Option value="tr">Türkçe</Option>
            </Dropdown>
          </div>
        </div>
      </section>

      {/* AI summary output language — reuses the same Language list as the full Meeting Angelo app. */}
      <section className={s.section}>
        <SectionTitle>{t("settings.summaryLanguage.sectionTitle")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
          <Text size={200} block style={{ color: "var(--colorNeutralForeground3)", marginBottom: "10px" }}>
            {t("settings.summaryLanguage.description")}
          </Text>
          {languageError && (
            <Text size={200} block style={{ color: "var(--colorPaletteRedForeground1)", marginBottom: "8px" }}>
              {languageError}
            </Text>
          )}
          {!languages ? (
            <div className={s.emptyCard}>
              <Spinner size="tiny" />
              <Text size={200}>{t("settings.summaryLanguage.loading")}</Text>
            </div>
          ) : (
            <div className={s.rowBetween}>
              <Dropdown
                size="small"
                style={{ minWidth: "220px" }}
                value={languages.find((l) => l.id === myLanguageId)?.name ?? ""}
                selectedOptions={myLanguageId ? [myLanguageId] : []}
                onOptionSelect={(_, data) => {
                  if (data.optionValue) void saveMyLanguage(data.optionValue);
                }}
              >
                {languages.map((l) => (
                  <Option key={l.id} value={l.id} text={`${l.name} (${l.shortName})`}>
                    {l.name} ({l.shortName})
                  </Option>
                ))}
              </Dropdown>
              {savingLanguage && <Spinner size="tiny" />}
              {!savingLanguage && languageSaved && (
                <Text size={200} style={{ color: "var(--colorPaletteGreenForeground1)" }}>
                  {t("settings.summaryLanguage.saved")}
                </Text>
              )}
            </div>
          )}
        </div>
      </section>

      {/* License / subscription */}
      <section className={s.section}>
        <SectionTitle>{t("settings.license.title")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
          <div className={s.row}>
            <IconTile variant={entitlement?.hasActiveSubscription ? "success" : "brand"}>
              <ShieldIcon />
            </IconTile>
            <div className={s.grow}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Text weight="semibold">{entitlement?.offerId ?? "Meeting Angelo Lite"}</Text>
                <Badge appearance="tint" color={statusColor(entitlement?.status)}>
                  {statusLabel(entitlement?.status, t)}
                </Badge>
                {entitlement?.isFreeTrial && (
                  <Badge appearance="tint" color="informative">
                    {t("settings.license.trial")}
                  </Badge>
                )}
              </div>
              <Text size={200} block style={{ marginTop: "4px", color: "var(--colorNeutralForeground3)" }}>
                {entitlement?.planId ? t("settings.license.plan", { planId: entitlement.planId }) : t("settings.license.noPlan")}
                {entitlement?.termEndDate
                  ? t("settings.license.renewsEnds", { date: new Date(entitlement.termEndDate).toLocaleDateString() })
                  : ""}
              </Text>
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
            {!entitlement?.hasActiveSubscription && (
              <Button
                appearance="primary"
                icon={<RocketIcon />}
                onClick={() => void openExternalLink(buyUrl, insideTeams)}
              >
                {t("settings.license.buyUpgrade")}
              </Button>
            )}
            <Button appearance="secondary" onClick={handleRefreshEntitlement} disabled={refreshing}>
              {refreshing ? <Spinner size="tiny" /> : t("settings.license.refreshStatus")}
            </Button>
          </div>
        </div>
      </section>

      {/* Meeting credits — my usage (everyone) */}
      {entitlement?.hasActiveSubscription && (
        <section className={s.section}>
          <SectionTitle>{t("settings.myCredits.title")}</SectionTitle>
          <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
            {myCredits ? (
              <>
                <div className={s.rowBetween}>
                  <Text weight="semibold">
                    {myCredits.monthlyLimit > 0
                      ? t("settings.myCredits.used", { used: myCredits.usedThisPeriod, limit: myCredits.monthlyLimit })
                      : t("settings.myCredits.usedNoLimit", { used: myCredits.usedThisPeriod })}
                  </Text>
                  <Badge appearance="tint" color={myCredits.monthlyLimit > 0 ? "brand" : "informative"}>
                    {myCredits.monthlyLimit > 0 ? t("settings.myCredits.personalLimit") : t("settings.myCredits.sharedPool")}
                  </Badge>
                </div>
                <ProgressBar
                  style={{ marginTop: "8px" }}
                  value={
                    myCredits.monthlyLimit > 0
                      ? Math.min(1, myCredits.usedThisPeriod / Math.max(1, myCredits.monthlyLimit))
                      : Math.min(1, myCredits.tenantUsedThisPeriod / Math.max(1, myCredits.tenantMonthlyCredits))
                  }
                  color={
                    (myCredits.monthlyLimit > 0 ? myCredits.personalRemaining : myCredits.tenantRemaining) === 0
                      ? "error"
                      : "brand"
                  }
                />
                <Text size={200} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
                  {myCredits.monthlyLimit > 0
                    ? t("settings.myCredits.withLimit", { limit: myCredits.monthlyLimit, remaining: myCredits.personalRemaining ?? 0 })
                    : t("settings.myCredits.withoutLimit", { remaining: myCredits.tenantRemaining, total: myCredits.tenantMonthlyCredits })}
                </Text>
              </>
            ) : (
              <div className={s.emptyCard}>
                <Spinner size="tiny" />
                <Text size={200}>{t("settings.myCredits.loading")}</Text>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Organization meeting credits — distribute the tenant pool among users. Admin/SuperAdmin only. */}
      {canManageOrgCredits && entitlement?.hasActiveSubscription && (
        <section className={s.section}>
          <SectionTitle>{t("settings.orgCredits.title")}</SectionTitle>
          <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
            {creditsError && (
              <Text size={200} block style={{ color: "var(--colorPaletteRedForeground1)", marginBottom: "10px" }}>
                {creditsError}
              </Text>
            )}
            {!tenantCredits ? (
              <div className={s.emptyCard}>
                <Spinner size="tiny" />
                <Text size={200}>{t("settings.orgCredits.loading")}</Text>
              </div>
            ) : (
              <>
                <div className={s.rowBetween}>
                  <Text weight="semibold">
                    {tenantCredits.planDisplayName ?? tenantCredits.planId ?? t("settings.orgCredits.currentPlan")}
                  </Text>
                  <Text size={200} style={{ color: "var(--colorNeutralForeground3)" }}>
                    {t("settings.orgCredits.usedThisMonth", { used: tenantCredits.usedThisPeriod, total: tenantCredits.monthlyMeetingCredits })}
                  </Text>
                </div>
                <ProgressBar
                  style={{ marginTop: "8px" }}
                  value={Math.min(1, tenantCredits.usedThisPeriod / Math.max(1, tenantCredits.monthlyMeetingCredits))}
                  color={tenantCredits.remainingThisPeriod === 0 ? "error" : "brand"}
                />
                <Text size={200} block style={{ marginTop: "8px", color: "var(--colorNeutralForeground3)" }}>
                  {t("settings.orgCredits.allocatedSummary", {
                    allocated: tenantCredits.allocatedTotal,
                    unallocated: tenantCredits.unallocatedTotal,
                  })}
                </Text>

                <Divider style={{ margin: "14px 0" }} />
                <Text weight="medium" block style={{ marginBottom: "8px" }}>
                  {t("settings.orgCredits.perPersonLimit")}
                </Text>
                <Text size={200} block style={{ marginBottom: "10px", color: "var(--colorNeutralForeground3)" }}>
                  {t("settings.orgCredits.perPersonNote")}
                </Text>
                {tenantCredits.allocations.map((row: UserCreditInfo, i: number) => (
                  <div key={row.userId}>
                    {i > 0 && <div className={s.divider} />}
                    <div className={s.row} style={{ padding: "8px 0", alignItems: "center" }}>
                      <div className={s.grow} style={{ minWidth: 0 }}>
                        <Text size={300} weight="medium" truncate wrap={false} block>
                          {[row.firstName, row.lastName].filter(Boolean).join(" ") || row.email}
                        </Text>
                        <Text size={200} truncate wrap={false} block style={{ color: "var(--colorNeutralForeground3)" }}>
                          {row.email} · {row.usedThisPeriod} {t("settings.orgCredits.used")}
                          {row.remainingThisPeriod !== null ? ` · ${row.remainingThisPeriod} ${t("settings.orgCredits.left")}` : ""}
                        </Text>
                      </div>
                      <Input
                        size="small"
                        type="number"
                        style={{ width: "70px" }}
                        value={allocationDrafts[row.userId] ?? String(row.monthlyLimit)}
                        onChange={(_, data) =>
                          setAllocationDrafts((d) => ({ ...d, [row.userId]: data.value }))
                        }
                      />
                      <Button
                        appearance="secondary"
                        size="small"
                        disabled={savingAllocationId === row.userId}
                        onClick={() => void saveAllocation(row.userId)}
                      >
                        {savingAllocationId === row.userId ? <Spinner size="tiny" /> : t("settings.orgCredits.save")}
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
      )}

      {/* Permissions */}
      <section className={s.section}>
        <SectionTitle>{t("settings.permissions.title")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
          <div className={s.row}>
            <IconTile variant="neutral">
              <ShieldIcon />
            </IconTile>
            <div className={s.grow}>
              <Text weight="semibold" block>
                {t("settings.permissions.signedInAs", { who: session.user.email || session.user.firstName })}
              </Text>
              <Text size={200} block style={{ marginTop: "6px", color: "var(--colorNeutralForeground2)" }}>
                {t("settings.permissions.requestsIntro")}
              </Text>
              <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                <li>
                  <Text size={200}>{t("settings.permissions.userRead")}</Text>
                </li>
                <li>
                  <Text size={200}>{t("settings.permissions.calendarsRead")}</Text>
                </li>
                <li>
                  <Text size={200}>
                    {t("settings.permissions.perMeeting")}
                  </Text>
                </li>
              </ul>
            </div>
          </div>
          <Divider style={{ margin: "12px 0" }} />
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <Button appearance="transparent" size="small" onClick={() => void openExternalLink(`${window.location.origin}/privacy.html`, insideTeams)}>
              {t("settings.permissions.privacyPolicy")}
            </Button>
            <Button appearance="transparent" size="small" onClick={() => void openExternalLink(`${window.location.origin}/terms.html`, insideTeams)}>
              {t("settings.permissions.termsOfUse")}
            </Button>
          </div>
        </div>
      </section>

      {/* Summary recipients */}
      <section className={s.section}>
        <SectionTitle>{t("settings.recipients.title")}</SectionTitle>
        <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
          {recipientRows.length === 0 && (
            <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
              {t("settings.recipients.empty")}
            </Text>
          )}
          {trackedRows.length === 0 && recipientRows.length > 0 && recipientRows.every((r) => !r.loading) && (
            <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
              {t("settings.recipients.noneTracked")}
            </Text>
          )}
          {recipientRows.map(
            (row) =>
              (row.loading || row.tracked) && (
                <div key={row.eventId} className={s.row} style={{ padding: "8px 0" }}>
                  <div className={s.grow} style={{ minWidth: 0 }}>
                    <Text size={300} weight="medium" truncate wrap={false} block>
                      {row.name}
                    </Text>
                    <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
                      {row.sendToAll ? t("settings.recipients.sentToEveryone") : t("settings.recipients.sentToYou")}
                    </Text>
                  </div>
                  {row.loading ? (
                    <Spinner size="tiny" />
                  ) : (
                    <Switch
                      checked={row.sendToAll}
                      disabled={row.saving}
                      onChange={(_, data) => void toggleRecipient(row, data.checked)}
                      aria-label={t("settings.recipients.sentToEveryone")}
                    />
                  )}
                </div>
              ),
          )}
          <Text size={100} block style={{ marginTop: "10px", color: "var(--colorNeutralForeground3)" }}>
            {t("settings.recipients.hint")}
          </Text>
        </div>
      </section>

      {/* Organization defaults — mirrors the org-wide preferences from the full Meeting Angelo app. Admin/SuperAdmin only. */}
      {isAdmin && (
        <section className={s.section}>
          <SectionTitle>{t("settings.orgDefaults.title")}</SectionTitle>
          <div className={mergeClasses(s.card, s.accentTop)} style={{ padding: "16px 18px" }}>
            {orgLoading && (
              <div className={s.emptyCard}>
                <Spinner size="tiny" />
                <Text size={200}>{t("settings.orgDefaults.loading")}</Text>
              </div>
            )}
            {orgError && (
              <Text size={200} block style={{ color: "var(--colorPaletteRedForeground1)", marginBottom: "10px" }}>
                {orgError}
              </Text>
            )}
            {!orgLoading && summaryTemplate !== null && deliveryMode !== null && (
              <>
                <div className={s.rowBetween} style={{ marginBottom: "14px" }}>
                  <div className={s.grow}>
                    <Text weight="medium" block>
                      {t("settings.orgDefaults.summaryTemplate")}
                    </Text>
                    <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
                      {t("settings.orgDefaults.summaryTemplateDesc")}
                    </Text>
                  </div>
                  <Dropdown
                    size="small"
                    style={{ minWidth: "170px" }}
                    value={summaryTemplateLabels[summaryTemplate]}
                    selectedOptions={[String(summaryTemplate)]}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) void saveOrgDefault({ summaryTemplate: Number(data.optionValue) });
                    }}
                  >
                    {Object.entries(summaryTemplateLabels).map(([value, label]) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                <Divider style={{ margin: "12px 0" }} />

                <div className={s.rowBetween}>
                  <div className={s.grow}>
                    <Text weight="medium" block>
                      {t("settings.orgDefaults.deliveryMode")}
                    </Text>
                    <Text size={200} block style={{ color: "var(--colorNeutralForeground3)" }}>
                      {t("settings.orgDefaults.deliveryModeDesc")}
                    </Text>
                  </div>
                  <Dropdown
                    size="small"
                    style={{ minWidth: "170px" }}
                    value={deliveryModeLabels[deliveryMode]}
                    selectedOptions={[String(deliveryMode)]}
                    onOptionSelect={(_, data) => {
                      if (data.optionValue) void saveOrgDefault({ meetingSendingType: Number(data.optionValue) });
                    }}
                  >
                    {Object.entries(deliveryModeLabels).map(([value, label]) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                {deliveryMode === 2 && (
                  <>
                    <Divider style={{ margin: "12px 0" }} />
                    <Text weight="medium" block style={{ marginBottom: "8px" }}>
                      {t("settings.orgDefaults.predefinedRecipients")}
                    </Text>
                    {predefined?.map((p) => (
                      <div key={p.id} className={s.rowBetween} style={{ padding: "6px 0", alignItems: "center" }}>
                        <div className={s.grow} style={{ minWidth: 0 }}>
                          <Text size={200} truncate wrap={false} block>
                            {p.name}
                          </Text>
                          <Text size={100} truncate wrap={false} block style={{ color: "var(--colorNeutralForeground3)" }}>
                            {p.email}
                          </Text>
                        </div>
                        <Button
                          appearance="transparent"
                          size="small"
                          disabled={predefinedBusy}
                          onClick={() => void removeRecipient(p.id)}
                        >
                          {t("settings.orgDefaults.remove")}
                        </Button>
                      </div>
                    ))}
                    {predefined?.length === 0 && (
                      <Text size={200} block style={{ color: "var(--colorNeutralForeground3)", marginBottom: "8px" }}>
                        {t("settings.orgDefaults.noPredefined")}
                      </Text>
                    )}
                    <div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
                      <Input
                        size="small"
                        placeholder={t("settings.orgDefaults.namePlaceholder")}
                        value={newName}
                        onChange={(_, data) => setNewName(data.value)}
                        style={{ flex: 1, minWidth: "120px" }}
                      />
                      <Input
                        size="small"
                        placeholder={t("settings.orgDefaults.emailPlaceholder")}
                        value={newEmail}
                        onChange={(_, data) => setNewEmail(data.value)}
                        style={{ flex: 1, minWidth: "160px" }}
                      />
                      <Button
                        appearance="secondary"
                        size="small"
                        disabled={predefinedBusy || !newEmail.trim() || !newName.trim()}
                        onClick={() => void addRecipient()}
                      >
                        {t("settings.orgDefaults.add")}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </section>
      )}

      <Footer>{t("settings.footer", { offerId: entitlement?.offerId ?? "paidapp-meetingangelo" })}</Footer>
    </main>
  );
}
